import { useState, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner, Button } from '@components/ui';
import { useEpics, useTasks, useSprints, useCreateEpic, useUpdateEpic, useDeleteEpic } from '@hooks';
import { useProjectStore } from '@store';
import EpicModal from '@components/features/EpicModal';
import { parseDay } from '@utils/calendarDate';

function TimelineBar({ epic, tasks, weeksToShow, onUpdateEpic, weekOffset = 0, todayWeekIndex = 0 }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(null); // 'left' | 'right' | null
  const [previewState, setPreviewState] = useState(null); // { startWeek, duration } for visual preview
  const barRef = useRef(null);
  const dragStartRef = useRef({ x: 0, startWeek: 0, duration: 0, containerWidth: 0 });

  const epicTasks = tasks.filter(t => t.epicId === epic.id);
  const completedTasks = epicTasks.filter(t => t.status === 'done').length;
  const totalTasks = epicTasks.length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // Use preview state during drag, otherwise use epic values (adjusted for weekOffset)
  const displayStartWeek = (previewState?.startWeek ?? epic.startWeek ?? 0) - weekOffset;
  const displayDuration = previewState?.duration ?? epic.duration ?? 4;
  const epicEndWeek = displayStartWeek + displayDuration;
  const isDelayed = progress < 100 && todayWeekIndex >= epicEndWeek;
  const left = (displayStartWeek / weeksToShow) * 100;
  const width = (displayDuration / weeksToShow) * 100;

  const getContainerWidth = useCallback(() => {
    if (barRef.current && barRef.current.parentElement) {
      return barRef.current.parentElement.offsetWidth;
    }
    return 1;
  }, []);

  const handleMouseDown = (e, type = 'move') => {
    e.preventDefault();
    e.stopPropagation();

    const containerWidth = getContainerWidth();

    dragStartRef.current = {
      x: e.clientX,
      startWeek: epic.startWeek || 0,
      duration: epic.duration || 4,
      containerWidth,
    };

    if (type === 'move') {
      setIsDragging(true);
    } else {
      setIsResizing(type);
    }

    const handleMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - dragStartRef.current.x;
      const weekWidth = dragStartRef.current.containerWidth / weeksToShow;
      const weeksDelta = Math.round(deltaX / weekWidth);

      if (type === 'move') {
        // Moving the entire bar
        const newStartWeek = Math.max(0, Math.min(weeksToShow - dragStartRef.current.duration, dragStartRef.current.startWeek + weeksDelta));
        setPreviewState({
          startWeek: newStartWeek,
          duration: dragStartRef.current.duration,
        });
      } else if (type === 'left') {
        // Resizing from left edge
        const newStartWeek = Math.max(0, Math.min(dragStartRef.current.startWeek + dragStartRef.current.duration - 1, dragStartRef.current.startWeek + weeksDelta));
        const newDuration = dragStartRef.current.duration - (newStartWeek - dragStartRef.current.startWeek);
        setPreviewState({
          startWeek: newStartWeek,
          duration: Math.max(1, newDuration),
        });
      } else if (type === 'right') {
        // Resizing from right edge
        const newDuration = Math.max(1, Math.min(weeksToShow - dragStartRef.current.startWeek, dragStartRef.current.duration + weeksDelta));
        setPreviewState({
          startWeek: dragStartRef.current.startWeek,
          duration: newDuration,
        });
      }
    };

    let lastMoveEvent = null;

    const wrappedHandleMove = (moveEvent) => {
      lastMoveEvent = moveEvent;
      handleMove(moveEvent);
    };

    const handleUp = () => {
      // Save to database only on mouse up using last known position
      if (lastMoveEvent) {
        const deltaX = lastMoveEvent.clientX - dragStartRef.current.x;
        const weekWidth = dragStartRef.current.containerWidth / weeksToShow;
        const weeksDelta = Math.round(deltaX / weekWidth);

        let finalStartWeek = dragStartRef.current.startWeek;
        let finalDuration = dragStartRef.current.duration;

        if (type === 'move') {
          finalStartWeek = Math.max(0, Math.min(weeksToShow - dragStartRef.current.duration, dragStartRef.current.startWeek + weeksDelta));
        } else if (type === 'left') {
          finalStartWeek = Math.max(0, Math.min(dragStartRef.current.startWeek + dragStartRef.current.duration - 1, dragStartRef.current.startWeek + weeksDelta));
          finalDuration = dragStartRef.current.duration - (finalStartWeek - dragStartRef.current.startWeek);
          finalDuration = Math.max(1, finalDuration);
        } else if (type === 'right') {
          finalDuration = Math.max(1, Math.min(weeksToShow - dragStartRef.current.startWeek, dragStartRef.current.duration + weeksDelta));
        }

        // Only update if changed
        if (finalStartWeek !== (epic.startWeek || 0) || finalDuration !== (epic.duration || 4)) {
          onUpdateEpic(epic.id, { startWeek: finalStartWeek, duration: finalDuration });
        }
      }

      setIsDragging(false);
      setIsResizing(null);
      setPreviewState(null);
      document.removeEventListener('mousemove', wrappedHandleMove);
      document.removeEventListener('mouseup', handleUp);
    };

    document.addEventListener('mousemove', wrappedHandleMove);
    document.addEventListener('mouseup', handleUp);
  };

  const resizeHandleStyle = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '8px',
    cursor: 'ew-resize',
    zIndex: 10,
  };

  return (
    <div
      ref={barRef}
      style={{
        position: 'absolute',
        left: `${left}%`,
        width: `${width}%`,
        height: '32px',
        backgroundColor: isDelayed ? 'rgba(239, 68, 68, 0.08)' : '#f1f5f9',
        borderRadius: '6px',
        overflow: 'visible',
        cursor: isDragging ? 'grabbing' : 'grab',
        border: (isDragging || isResizing) ? '2px solid #667eea' : isDelayed ? '2px solid #ef4444' : '1px solid #e2e8f0',
        boxShadow: (isDragging || isResizing) ? '0 4px 12px rgba(102, 126, 234, 0.3)' : isDelayed ? '0 0 8px rgba(239, 68, 68, 0.2)' : 'none',
        userSelect: 'none',
      }}
      title={`${epic.name}: ${Math.round(progress)}% completado${isDelayed ? ' - Atrasado' : ''} - Arrastra para mover, bordes para redimensionar`}
      onMouseDown={(e) => handleMouseDown(e, 'move')}
    >
      {/* Left resize handle */}
      <div
        style={{ ...resizeHandleStyle, left: 0 }}
        onMouseDown={(e) => handleMouseDown(e, 'left')}
      />

      {/* Right resize handle */}
      <div
        style={{ ...resizeHandleStyle, right: 0 }}
        onMouseDown={(e) => handleMouseDown(e, 'right')}
      />

      {/* Progress fill */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${progress}%`,
          backgroundColor: isDelayed ? '#ef4444' : (epic.color || '#667eea'),
          opacity: 0.9,
          borderRadius: '4px 0 0 4px',
          pointerEvents: 'none',
        }}
      />
      {/* Epic name */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          padding: '0 12px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        <span
          style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#1e293b',
            whiteSpace: 'nowrap',
            textShadow: progress > 30 ? '0 0 3px rgba(255,255,255,0.8)' : 'none',
          }}
        >
          {epic.name}
        </span>
        <span
          style={{
            fontSize: '10px',
            color: '#64748b',
            flexShrink: 0,
          }}
        >
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
}

TimelineBar.propTypes = {
  epic: PropTypes.object.isRequired,
  tasks: PropTypes.array.isRequired,
  weeksToShow: PropTypes.number.isRequired,
  onUpdateEpic: PropTypes.func.isRequired,
  weekOffset: PropTypes.number,
  todayWeekIndex: PropTypes.number,
};

function EpicDetailCard({ epic, tasks, onEdit, onDelete, isDelayed = false }) {
  const epicTasks = tasks.filter(t => t.epicId === epic.id);
  const completedTasks = epicTasks.filter(t => t.status === 'done').length;
  const inProgressTasks = epicTasks.filter(t => t.status === 'progress' || t.status === 'review').length;
  const pendingTasks = epicTasks.filter(t => t.status === 'backlog' || t.status === 'todo').length;
  const totalTasks = epicTasks.length;
  const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const status = isDelayed ? 'delayed' : progress === 100 ? 'completed' : progress > 0 ? 'in_progress' : 'not_started';
  const statusConfig = {
    not_started: { color: '#94a3b8', label: 'Sin Iniciar' },
    in_progress: { color: '#3b82f6', label: 'En Progreso' },
    completed: { color: '#10b981', label: 'Completado' },
    delayed: { color: '#ef4444', label: 'Atrasado' },
  }[status];

  return (
    <div
      style={{
        backgroundColor: isDelayed ? 'rgba(239, 68, 68, 0.03)' : 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: isDelayed ? '2px solid rgba(239, 68, 68, 0.3)' : '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Color indicator */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '4px',
          backgroundColor: isDelayed ? '#ef4444' : (epic.color || '#667eea'),
        }}
      />
      <div style={{ paddingLeft: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0 }}>{epic.name}</h3>
            {epic.description && (
              <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0', lineHeight: 1.4 }}>
                {epic.description.length > 100 ? epic.description.slice(0, 100) + '...' : epic.description}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '500',
                padding: '4px 8px',
                borderRadius: '6px',
                backgroundColor: statusConfig.color + '15',
                color: statusConfig.color,
              }}
            >
              {statusConfig.label}
            </span>
            <button
              onClick={() => onEdit(epic)}
              style={{
                padding: '4px 8px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#f1f5f9',
                color: '#64748b',
                cursor: 'pointer',
                fontSize: '12px',
              }}
              title="Editar épica"
            >
              Editar
            </button>
            <button
              onClick={() => onDelete(epic.id)}
              style={{
                padding: '4px 8px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#fef2f2',
                color: '#ef4444',
                cursor: 'pointer',
                fontSize: '12px',
              }}
              title="Eliminar épica"
            >
              Eliminar
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
            <span style={{ color: '#64748b' }}>Progreso</span>
            <span style={{ color: '#475569', fontWeight: '500' }}>{progress}%</span>
          </div>
          <div style={{ backgroundColor: '#e2e8f0', borderRadius: '4px', height: '6px' }}>
            <div
              style={{
                height: '6px',
                borderRadius: '4px',
                width: `${progress}%`,
                backgroundColor: isDelayed ? '#ef4444' : (epic.color || '#667eea'),
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>

        {/* Task summary */}
        <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
          <span style={{ color: '#64748b' }}>📋 {pendingTasks}</span>
          <span style={{ color: '#f59e0b' }}>⚡ {inProgressTasks}</span>
          <span style={{ color: '#10b981' }}>✅ {completedTasks}</span>
          <span style={{ color: '#94a3b8', marginLeft: 'auto' }}>{totalTasks} total</span>
        </div>
      </div>
    </div>
  );
}

EpicDetailCard.propTypes = {
  epic: PropTypes.object.isRequired,
  tasks: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isDelayed: PropTypes.bool,
};

export default function RoadmapView({ projectId }) {
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'list'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEpic, setEditingEpic] = useState(null);

  const { selectedProject } = useProjectStore();

  const { data: epicsData, isLoading: epicsLoading } = useEpics({ projectId }, { enabled: !!projectId });
  const { data: tasksData, isLoading: tasksLoading } = useTasks({ projectId }, { enabled: !!projectId });
  const { data: sprintsData } = useSprints({ projectId }, { enabled: !!projectId });

  const createEpic = useCreateEpic();
  const updateEpic = useUpdateEpic();
  const deleteEpic = useDeleteEpic();

  const epics = epicsData?.data || [];
  const tasks = tasksData?.data || [];
  const sprints = sprintsData?.data || [];

  const isLoading = epicsLoading || tasksLoading;

  const handleOpenModal = (epic = null) => {
    setEditingEpic(epic);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEpic(null);
  };

  const handleSubmit = async (data) => {
    if (editingEpic) {
      await updateEpic.mutateAsync({ id: editingEpic.id, data });
    } else {
      await createEpic.mutateAsync({ ...data, projectId });
    }
    handleCloseModal();
  };

  const handleDeleteEpic = async (epicId) => {
    if (window.confirm('¿Estás seguro de eliminar esta épica? Las tareas asociadas no se eliminarán.')) {
      await deleteEpic.mutateAsync(epicId);
    }
  };

  // Handle drag updates from timeline
  const handleUpdateEpicTimeline = useCallback(async (epicId, data) => {
    await updateEpic.mutateAsync({ id: epicId, data });
  }, [updateEpic]);

  // Calculate timeline parameters - starts from earliest epic
  const timelineConfig = useMemo(() => {
    const now = new Date();

    // Usar fecha de inicio del proyecto si existe
    let baseDate;
    if (selectedProject?.startDate) {
      baseDate = parseDay(selectedProject.startDate);
    } else {
      baseDate = new Date(now);
    }

    // Ajustar al lunes de esa semana
    const baseDayOfWeek = baseDate.getDay();
    const baseDaysToSubtract = baseDayOfWeek === 0 ? 6 : baseDayOfWeek - 1;
    const baseMonday = new Date(baseDate);
    baseMonday.setDate(baseDate.getDate() - baseDaysToSubtract);
    baseMonday.setHours(0, 0, 0, 0);

    // Find the earliest epic startWeek
    const minStartWeek = epics.length > 0
      ? Math.min(...epics.map(e => e.startWeek ?? 0))
      : 0;

    // Find the latest epic end (startWeek + duration)
    const maxEndWeek = epics.length > 0
      ? Math.max(...epics.map(e => (e.startWeek ?? 0) + (e.duration ?? 4)))
      : minStartWeek + 24;

    // Start timeline from the earliest epic's week
    const startOfWeek = new Date(baseMonday);
    startOfWeek.setDate(baseMonday.getDate() + (minStartWeek * 7));

    // Ensure enough weeks to cover all epics plus padding
    const weeksToShow = Math.max(24, maxEndWeek - minStartWeek + 4);
    const weeks = [];

    // Calculate current week's Monday for comparison
    const nowMonday = new Date(now);
    const nowDayOfWeek = now.getDay();
    const nowDaysToSubtract = nowDayOfWeek === 0 ? 6 : nowDayOfWeek - 1;
    nowMonday.setDate(now.getDate() - nowDaysToSubtract);
    nowMonday.setHours(0, 0, 0, 0);

    let lastYear = null;
    for (let i = 0; i < weeksToShow; i++) {
      const weekStart = new Date(startOfWeek);
      weekStart.setDate(startOfWeek.getDate() + (i * 7));
      const year = weekStart.getFullYear();
      const showYear = year !== lastYear;
      lastYear = year;

      const isCurrentWeek = weekStart.getTime() === nowMonday.getTime();

      weeks.push({
        start: weekStart,
        label: weekStart.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
        year: showYear ? year : null,
        isCurrentWeek,
      });
    }

    // Calculate today's exact position as percentage and week index
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const msSinceStart = now.getTime() - startOfWeek.getTime();
    const todayPositionPercent = Math.max(0, Math.min(100, (msSinceStart / msPerWeek / weeksToShow) * 100));
    const todayWeekIndex = Math.floor(msSinceStart / msPerWeek);

    return { weeks, weeksToShow, startDate: startOfWeek, weekOffset: minStartWeek, todayPositionPercent, todayWeekIndex };
  }, [epics, selectedProject?.startDate]);

  // Calculate overall progress
  const metrics = useMemo(() => {
    const totalEpicTasks = tasks.filter(t => t.epicId).length;
    const completedEpicTasks = tasks.filter(t => t.epicId && t.status === 'done').length;
    const overallProgress = totalEpicTasks > 0 ? Math.round((completedEpicTasks / totalEpicTasks) * 100) : 0;

    const epicsByStatus = epics.reduce((acc, epic) => {
      const epicTasks = tasks.filter(t => t.epicId === epic.id);
      const completedTasks = epicTasks.filter(t => t.status === 'done').length;
      const progress = epicTasks.length > 0 ? (completedTasks / epicTasks.length) * 100 : 0;

      const status = progress === 100 ? 'completed' : progress > 0 ? 'in_progress' : 'not_started';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return { overallProgress, epicsByStatus };
  }, [epics, tasks]);

  // Calculate parallel work zones (weeks where multiple epics overlap, adjusted for weekOffset)
  const parallelZones = useMemo(() => {
    const weekCounts = {};
    const offset = timelineConfig.weekOffset;

    epics.forEach(epic => {
      const startWeek = (epic.startWeek || 0) - offset;
      const duration = epic.duration || 4;

      for (let w = startWeek; w < startWeek + duration && w < timelineConfig.weeksToShow; w++) {
        if (w >= 0) {
          weekCounts[w] = (weekCounts[w] || 0) + 1;
        }
      }
    });

    // Find zones where 2+ epics overlap
    const zones = [];
    let currentZone = null;

    for (let w = 0; w < timelineConfig.weeksToShow; w++) {
      const count = weekCounts[w] || 0;
      if (count >= 2) {
        if (!currentZone) {
          currentZone = { start: w, count };
        }
        currentZone.end = w;
        currentZone.maxCount = Math.max(currentZone.maxCount || count, count);
      } else if (currentZone) {
        zones.push(currentZone);
        currentZone = null;
      }
    }
    if (currentZone) zones.push(currentZone);

    return zones;
  }, [epics, timelineConfig.weeksToShow, timelineConfig.weekOffset]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '256px' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>Roadmap del Proyecto</h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0 0' }}>
            {epics.length} épicas • {metrics.overallProgress}% completado
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setViewMode('timeline')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              backgroundColor: viewMode === 'timeline' ? '#667eea' : '#f1f5f9',
              color: viewMode === 'timeline' ? 'white' : '#475569',
            }}
          >
            📅 Timeline
          </button>
          <button
            onClick={() => setViewMode('list')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              backgroundColor: viewMode === 'list' ? '#667eea' : '#f1f5f9',
              color: viewMode === 'list' ? 'white' : '#475569',
            }}
          >
            📋 Lista
          </button>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            + Nueva Épica
          </Button>
        </div>
      </div>

      {/* Overall Progress Card */}
      <Card padding="lg" className="mb-6">
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
              Progreso General del Roadmap
            </h3>
            <div style={{ backgroundColor: '#e2e8f0', borderRadius: '6px', height: '12px', marginBottom: '16px' }}>
              <div
                style={{
                  height: '12px',
                  borderRadius: '6px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  width: `${metrics.overallProgress}%`,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '24px', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }} />
                <span style={{ color: '#475569' }}>{metrics.epicsByStatus.completed || 0} completados</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#3b82f6' }} />
                <span style={{ color: '#475569' }}>{metrics.epicsByStatus.in_progress || 0} en progreso</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#94a3b8' }} />
                <span style={{ color: '#475569' }}>{metrics.epicsByStatus.not_started || 0} sin iniciar</span>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '0 24px', borderLeft: '1px solid #e2e8f0' }}>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#667eea', margin: 0 }}>
              {metrics.overallProgress}%
            </p>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>Completado</p>
          </div>
        </div>
      </Card>

      {viewMode === 'timeline' ? (
        /* Timeline View */
        <Card padding="lg">
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Timeline de Épicas
          </h3>

          {/* Scrollable container */}
          <div
            style={{
              maxHeight: '500px',
              overflowY: 'auto',
              overflowX: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
            }}
          >
            {/* Inner content with minimum width for horizontal scroll */}
            <div style={{ minWidth: `${160 + (timelineConfig.weeksToShow * 60) + 16}px` }}>
              {/* Timeline header - weeks (sticky) */}
              <div
                style={{
                  display: 'flex',
                  borderBottom: '1px solid #e2e8f0',
                  backgroundColor: 'white',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                }}
              >
                <div style={{ width: '160px', flexShrink: 0, padding: '8px 0', backgroundColor: 'white' }} />
                <div style={{ display: 'flex', width: `${timelineConfig.weeksToShow * 60}px`, position: 'relative' }}>
                  {timelineConfig.weeks.map((week, i) => (
                    <div
                      key={i}
                      style={{
                        width: '60px',
                        flexShrink: 0,
                        textAlign: 'center',
                        padding: '6px 4px',
                        fontSize: '11px',
                        color: week.isCurrentWeek ? '#667eea' : '#64748b',
                        fontWeight: week.isCurrentWeek ? '600' : '400',
                        borderLeft: i > 0 ? '1px solid #f1f5f9' : 'none',
                        backgroundColor: week.isCurrentWeek ? '#667eea08' : 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '1px',
                      }}
                    >
                      {week.year && (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '700',
                          color: week.isCurrentWeek ? '#667eea' : '#475569',
                          lineHeight: '1',
                        }}>
                          {week.year}
                        </span>
                      )}
                      <span>{week.label}</span>
                    </div>
                  ))}
                  {/* Today marker in header */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${timelineConfig.todayPositionPercent}%`,
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      backgroundColor: '#ef4444',
                      zIndex: 20,
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              </div>

              {/* Epic rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 0' }}>
                {epics.map((epic) => {
                  const epicTasks = tasks.filter(t => t.epicId === epic.id);
                  const completedTasks = epicTasks.filter(t => t.status === 'done').length;
                  const progress = epicTasks.length > 0 ? Math.round((completedTasks / epicTasks.length) * 100) : 0;
                  const epicEndWeek = ((epic.startWeek ?? 0) - timelineConfig.weekOffset) + (epic.duration ?? 4);
                  const isEpicDelayed = progress < 100 && timelineConfig.todayWeekIndex >= epicEndWeek;

                  return (
                    <div key={epic.id} style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                      {/* Epic name (sticky left) */}
                      <div
                        style={{
                          width: '160px',
                          flexShrink: 0,
                          paddingRight: '16px',
                          paddingLeft: '8px',
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'white',
                          zIndex: 5,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '2px',
                              backgroundColor: isEpicDelayed ? '#ef4444' : (epic.color || '#667eea'),
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: '500',
                              color: isEpicDelayed ? '#ef4444' : '#1e293b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {epic.name}
                          </span>
                          {isEpicDelayed && (
                            <span
                              style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                color: '#ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                padding: '1px 6px',
                                borderRadius: '9999px',
                                flexShrink: 0,
                                lineHeight: '1.4',
                              }}
                            >
                              Atrasado
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Timeline bar area */}
                      <div
                        style={{
                          width: `${timelineConfig.weeksToShow * 60}px`,
                          flexShrink: 0,
                          height: '40px',
                          position: 'relative',
                          backgroundColor: '#fafafa',
                          borderRadius: '4px',
                          marginRight: '8px',
                        }}
                      >
                        {/* Parallel work zone indicators */}
                        {parallelZones.map((zone, idx) => (
                          <div
                            key={idx}
                            style={{
                              position: 'absolute',
                              left: `${(zone.start / timelineConfig.weeksToShow) * 100}%`,
                              width: `${((zone.end - zone.start + 1) / timelineConfig.weeksToShow) * 100}%`,
                              top: 0,
                              bottom: 0,
                              backgroundColor: zone.maxCount >= 3 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(99, 102, 241, 0.1)',
                              borderLeft: '2px dashed',
                              borderRight: '2px dashed',
                              borderColor: zone.maxCount >= 3 ? '#f59e0b' : '#6366f1',
                              pointerEvents: 'none',
                            }}
                            title={`${zone.maxCount} épicas en paralelo`}
                          />
                        ))}
                        {/* Today marker line */}
                        <div
                          style={{
                            position: 'absolute',
                            left: `${timelineConfig.todayPositionPercent}%`,
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            backgroundColor: '#ef4444',
                            zIndex: 15,
                            pointerEvents: 'none',
                            boxShadow: '0 0 4px rgba(239, 68, 68, 0.4)',
                          }}
                        />
                        <TimelineBar
                          epic={epic}
                          tasks={tasks}
                          weeksToShow={timelineConfig.weeksToShow}
                          onUpdateEpic={handleUpdateEpicTimeline}
                          weekOffset={timelineConfig.weekOffset}
                          todayWeekIndex={timelineConfig.todayWeekIndex}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div style={{ marginTop: '16px', display: 'flex', gap: '24px', alignItems: 'center', fontSize: '12px', flexWrap: 'wrap' }}>
            {/* Today marker legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '2px',
                height: '16px',
                backgroundColor: '#ef4444',
                boxShadow: '0 0 4px rgba(239, 68, 68, 0.4)',
              }} />
              <span style={{ color: '#ef4444', fontWeight: '500' }}>Hoy</span>
            </div>

            {/* Delayed legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '16px',
                height: '16px',
                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                border: '2px solid #ef4444',
                borderRadius: '4px',
              }} />
              <span style={{ color: '#ef4444', fontWeight: '500' }}>Atrasado</span>
            </div>

            {/* Parallel work legend */}
            {parallelZones.length > 0 && (
              <>
                <span style={{ color: '#cbd5e1' }}>|</span>
                <span style={{ color: '#64748b', fontWeight: '500' }}>Trabajo en paralelo:</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    border: '2px dashed #6366f1',
                    borderRadius: '2px'
                  }} />
                  <span style={{ color: '#6366f1' }}>2 épicas</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    border: '2px dashed #f59e0b',
                    borderRadius: '2px'
                  }} />
                  <span style={{ color: '#f59e0b' }}>3+ épicas</span>
                </div>
              </>
            )}
          </div>

          {/* Sprint markers if available */}
          {sprints.length > 0 && (
            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '500', color: '#64748b', marginBottom: '8px' }}>
                Sprints
              </h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {sprints.slice(0, 5).map((sprint) => (
                  <span
                    key={sprint.id}
                    style={{
                      fontSize: '12px',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      backgroundColor: sprint.status === 'ACTIVE' ? '#667eea15' : '#f1f5f9',
                      color: sprint.status === 'ACTIVE' ? '#667eea' : '#64748b',
                      fontWeight: sprint.status === 'ACTIVE' ? '600' : '400',
                    }}
                  >
                    {sprint.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {epics.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <p style={{ color: '#64748b', fontSize: '14px' }}>No hay épicas definidas en el roadmap</p>
            </div>
          )}
        </Card>
      ) : (
        /* List View */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {epics.map((epic) => {
            const epicEndWeek = ((epic.startWeek ?? 0) - timelineConfig.weekOffset) + (epic.duration ?? 4);
            const epicTasksList = tasks.filter(t => t.epicId === epic.id);
            const epicDone = epicTasksList.filter(t => t.status === 'done').length;
            const epicProg = epicTasksList.length > 0 ? (epicDone / epicTasksList.length) * 100 : 0;
            const isEpicDelayed = epicProg < 100 && timelineConfig.todayWeekIndex >= epicEndWeek;

            return (
            <EpicDetailCard
              key={epic.id}
              epic={epic}
              tasks={tasks}
              onEdit={handleOpenModal}
              onDelete={handleDeleteEpic}
              isDelayed={isEpicDelayed}
            />
            );
          })}
          {epics.length === 0 && (
            <Card padding="lg" className="col-span-full">
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <p style={{ color: '#64748b', fontSize: '14px' }}>No hay épicas definidas en el roadmap</p>
              </div>
            </Card>
          )}
        </div>
      )}

      <EpicModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        epic={editingEpic}
        isLoading={createEpic.isPending || updateEpic.isPending}
      />
    </div>
  );
}

RoadmapView.propTypes = {
  projectId: PropTypes.number,
};
